import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from './client/Navbar';
import hotelData from './client/HotelPages/hotelpageData.json';
import { SITE_URL, SITE_NAME, ROBOTS_NOINDEX } from './common/seoConstants';

const NotFound = () => {
  return (
    <>
      <Helmet>
        <title>404 — Page Not Found | {SITE_NAME}</title>
        <meta
          name="description"
          content="Sorry, this page could not be found. Explore Friends Hotel properties in Pelling, Rinchenpong and Gangtok, or browse our Sikkim tour packages."
        />
        <meta name="robots" content={ROBOTS_NOINDEX} />
        <link rel="canonical" href={`${SITE_URL}/`} />
      </Helmet>
      <Navbar />
      <div style={styles.wrapper}>
        {/* Background blobs */}
        <div style={{ ...styles.blob, top: '4%',  left: '8%',  width: 380, height: 380, background: 'rgba(99,179,237,0.22)' }} />
        <div style={{ ...styles.blob, top: '18%', right: '6%', width: 260, height: 260, background: 'rgba(167,139,250,0.18)' }} />
        <div style={{ ...styles.blob, top: '60%', left: '50%', width: 200, height: 200, background: 'rgba(251,191,36,0.15)' }} />
        <div style={{ ...styles.blob, bottom: '5%', left: '20%', width: 300, height: 300, background: 'rgba(52,211,153,0.15)' }} />

        {/* 404 Hero Card */}
        <div style={styles.heroCard}>
          <div style={styles.codeWrap}>
            <span style={styles.codeDigit}>4</span>
            <span style={styles.codeZero}>0</span>
            <span style={styles.codeDigit}>4</span>
          </div>
          <div style={styles.divider} />
          <h2 style={styles.heading}>Page Not Found</h2>
          <p style={styles.subtext}>
            Oops! The page you're looking for doesn't exist or has been moved.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={styles.homeBtn}
              onMouseEnter={e => Object.assign(e.currentTarget.style, styles.homeBtnHover)}
              onMouseLeave={e => Object.assign(e.currentTarget.style, styles.homeBtn)}
            >
              ← Back to Home
            </Link>
            <Link
              to="/tour"
              style={{ ...styles.homeBtn, background: 'linear-gradient(135deg, #f59e0b, #d97706)', boxShadow: '0 4px 18px rgba(217,119,6,0.35)' }}
            >
              Tour Packages from ₹3,099 →
            </Link>
          </div>
        </div>

        {/* Explore Our Hotels */}
        <div style={styles.exploreSection}>
          <div style={styles.exploreLabelWrap}>
            <div style={styles.explorePill}>Our Properties</div>
            <h3 style={styles.exploreTitle}>Explore Our Hotels</h3>
            <p style={styles.exploreSubtext}>
              While you're here, discover our handpicked stays across Sikkim
            </p>
          </div>

          <div style={styles.grid}>
            {hotelData.map((hotel) => {
              const [name, location] = hotel.welcome.split(',');
              return (
                <Link
                  key={hotel.slug}
                  to={`/Hotel/${hotel.slug}`}
                  style={styles.card}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.06)';
                    e.currentTarget.querySelector('.card-img').style.transform = 'scale(1.06)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = styles.card.boxShadow;
                    e.currentTarget.querySelector('.card-img').style.transform = 'scale(1)';
                  }}
                >
                  {/* Image */}
                  <div style={styles.imgWrap}>
                    <img
                      className="card-img"
                      src={hotel.heroimg}
                      alt={hotel.welcome}
                      style={styles.cardImg}
                      loading="lazy"
                    />
                    {/* Gradient overlay */}
                    <div style={styles.imgOverlay} />
                    {/* Location pill on image */}
                    <div style={styles.locationPill}>
                      <span style={styles.locationDot} />
                      {location?.trim()}
                    </div>
                    {/* Bookable badge */}
                    {hotel.bookable === false && (
                      <div style={styles.unavailableBadge}>
                        <span style={styles.closedDot} />
                        Closed
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div style={styles.cardBody}>
                    <h4 style={styles.hotelName}>{name?.trim()}</h4>
                    <p style={styles.hotelDesc}>
                      {hotel.sub_welcome_note?.slice(0, 90)}…
                    </p>
                    <div style={styles.cardFooter}>
                      <span style={styles.roomCount}>
                        {hotel.rooms?.length} Room Types
                      </span>
                      <span style={styles.viewBtn}>
                        View Hotel →
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: 'linear-gradient(145deg, #e0f2fe 0%, #f0fdf4 45%, #faf5ff 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    paddingBottom: 80,
  },
  blob: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(72px)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  /* 404 hero */
  heroCard: {
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255,255,255,0.58)',
    backdropFilter: 'blur(22px)',
    WebkitBackdropFilter: 'blur(22px)',
    border: '1.5px solid rgba(255,255,255,0.78)',
    borderRadius: 28,
    padding: '52px 64px',
    maxWidth: 500,
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
    margin: '64px auto 0',
  },
  codeWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  codeDigit: {
    fontSize: 96,
    fontWeight: 800,
    lineHeight: 1,
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  codeZero: {
    fontSize: 96,
    fontWeight: 800,
    lineHeight: 1,
    background: 'linear-gradient(135deg, #06b6d4, #10b981)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  divider: {
    width: 60,
    height: 3,
    borderRadius: 99,
    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
    margin: '18px auto 22px',
    opacity: 0.6,
  },
  heading: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 10px',
    letterSpacing: '-0.3px',
  },
  subtext: {
    fontSize: 15,
    color: '#64748b',
    lineHeight: 1.65,
    margin: '0 0 32px',
  },
  homeBtn: {
    display: 'inline-block',
    padding: '13px 32px',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: 50,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.3px',
    boxShadow: '0 4px 18px rgba(99,102,241,0.35)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  homeBtnHover: {
    display: 'inline-block',
    padding: '13px 32px',
    background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: 50,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '0.3px',
    boxShadow: '0 6px 24px rgba(99,102,241,0.5)',
    transform: 'translateY(-2px)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },

  /* Explore section */
  exploreSection: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 1160,
    padding: '64px 24px 0',
  },
  exploreLabelWrap: {
    textAlign: 'center',
    marginBottom: 40,
  },
  explorePill: {
    display: 'inline-block',
    padding: '5px 16px',
    background: 'rgba(99,102,241,0.1)',
    border: '1px solid rgba(99,102,241,0.22)',
    borderRadius: 50,
    fontSize: 12,
    fontWeight: 600,
    color: '#6366f1',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  exploreTitle: {
    fontSize: 34,
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 10px',
    letterSpacing: '-0.5px',
  },
  exploreSubtext: {
    fontSize: 15,
    color: '#64748b',
    margin: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: 24,
  },

  /* Hotel card */
  card: {
    background: 'rgba(255,255,255,0.55)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    border: '1.5px solid rgba(255,255,255,0.75)',
    borderRadius: 22,
    overflow: 'hidden',
    textDecoration: 'none',
    color: 'inherit',
    boxShadow: '0 6px 30px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
    transition: 'transform 0.22s ease, box-shadow 0.22s ease',
    display: 'flex',
    flexDirection: 'column',
  },
  imgWrap: {
    position: 'relative',
    height: 200,
    overflow: 'hidden',
  },
  cardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
  imgOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.38) 0%, transparent 55%)',
  },
  locationPill: {
    position: 'absolute',
    bottom: 12,
    left: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'rgba(255,255,255,0.22)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: 50,
    padding: '4px 11px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    letterSpacing: '0.2px',
  },
  locationDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#34d399',
    flexShrink: 0,
  },
  unavailableBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(239,68,68,0.88)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    borderRadius: 50,
    padding: '4px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '0.3px',
  },
  closedDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#fff',
    flexShrink: 0,
    boxShadow: '0 0 0 2px rgba(255,255,255,0.4)',
  },
  cardBody: {
    padding: '18px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  hotelName: {
    fontSize: 17,
    fontWeight: 700,
    color: '#0f172a',
    margin: '0 0 7px',
    letterSpacing: '-0.2px',
  },
  hotelDesc: {
    fontSize: 13,
    color: '#64748b',
    lineHeight: 1.6,
    margin: '0 0 16px',
    flex: 1,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 12,
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  roomCount: {
    fontSize: 12,
    color: '#94a3b8',
    fontWeight: 500,
  },
  viewBtn: {
    fontSize: 13,
    fontWeight: 700,
    color: '#6366f1',
    letterSpacing: '0.1px',
  },
};

export default NotFound;
